import { useEffect } from "react";
import { NavigationProvider, useNavigation } from "./app/navigation";
import { WorkspaceProvider } from "./app/workspace";
import { CloseTodayPage } from "./pages/CloseTodayPage";
import { ClassSheetPage } from "./pages/ClassSheetPage";
import { LandingPage } from "./pages/LandingPage";
import { OnboardingSetupPage, StartPage } from "./pages/OnboardingPages";
import {
  BillingPage,
  ClassesPage,
  ExceptionPage,
  GuardianDraftsPage,
  PayrollPage,
  PeriodClosePage,
  RecordsPage,
  StudentLedgerPage,
  StudentsPage,
} from "./pages/OperationsPages";
import {
  DataSettingsPage,
  ImportPage,
  PlanSettingsPage,
  TeamSettingsPage,
} from "./pages/SettingsPages";
import {
  DiagnosisPage,
  GuardianLinkPage,
  NotFoundPage,
  PricingPage,
  PrivacyPage,
  SignInPage,
  StatusPage,
  SupportPage,
  TermsPage,
} from "./pages/PublicAndSupportPages";

const PUBLIC_ROUTE_META: Record<
  string,
  { title: string; description: string }
> = {
  "/": {
    title: "수업마침 — 학원·공부방 운영 마감",
    description:
      "출결 한 번으로 보강·안내·회차·수납의 다음 일을 모으고 오늘 운영을 끝내는 공부방·교습소·학원 운영 서비스",
  },
  "/diagnosis": {
    title: "학원 운영 진단 — 수업 뒤 남은 일 6문항 | 수업마침",
    description:
      "이메일 없이 1분 안에 출결·보강·수납·강사 기록의 중복 업무를 확인하고 바로 결과를 봅니다.",
  },
  "/pricing": {
    title: "수업마침 이용 기준 — 초기 공개 기간 무료",
    description:
      "카드 등록 없이 실제 운영 흐름을 확인하고, 전체 내보내기와 가져오기 되돌리기를 이용할 수 있습니다.",
  },
  "/privacy": {
    title: "개인정보 처리방침 | 수업마침",
    description: "수업마침이 다루는 정보, 보관 기간, 내보내기와 삭제 기준을 확인합니다.",
  },
  "/terms": {
    title: "이용약관 | 수업마침",
    description: "수업마침의 역할, 계정과 기록, 무료 공개 운영과 복구 기준을 확인합니다.",
  },
  "/support": {
    title: "수업마침 도움과 문의",
    description: "제품 안에 먼저 저장되는 문의와 현재 지원·복구 경로를 확인합니다.",
  },
  "/status": {
    title: "수업마침 서비스 상태",
    description: "수업마침 웹·데이터베이스·외부 연결의 현재 운영 상태를 확인합니다.",
  },
};

function useRouteMetadata(pathname: string) {
  useEffect(() => {
    const route = PUBLIC_ROUTE_META[pathname];
    const title = route?.title ?? "수업마침 운영 화면";
    const description =
      route?.description ?? "로그인한 학원과 목적 제한 링크만 사용하는 수업마침 운영 화면입니다.";
    const canonicalUrl = route ? `https://sueopmachim.com${pathname}` : null;
    document.title = title;

    const upsertMeta = (selector: string, attribute: string, value: string) => {
      const element = document.head.querySelector<HTMLMetaElement>(selector);
      if (element) element.setAttribute(attribute, value);
    };
    upsertMeta('meta[name="description"]', "content", description);
    upsertMeta(
      'meta[name="robots"]',
      "content",
      route ? "index,follow,max-image-preview:large" : "noindex,nofollow",
    );
    upsertMeta('meta[property="og:title"]', "content", title);
    upsertMeta('meta[property="og:description"]', "content", description);
    if (canonicalUrl) {
      let ogUrl = document.head.querySelector<HTMLMetaElement>(
        'meta[property="og:url"]',
      );
      if (!ogUrl) {
        ogUrl = document.createElement("meta");
        ogUrl.setAttribute("property", "og:url");
        document.head.append(ogUrl);
      }
      ogUrl.content = canonicalUrl;
      let canonical = document.head.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]',
      );
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.append(canonical);
      }
      canonical.href = canonicalUrl;
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }
  }, [pathname]);
}

function Routes() {
  const { pathname } = useNavigation();
  useRouteMetadata(pathname);
  if (pathname === "/") return <LandingPage />;
  if (pathname === "/start") return <StartPage />;
  if (pathname === "/onboarding/setup") return <OnboardingSetupPage />;
  if (pathname === "/try/today") return <ClassSheetPage />;
  const classMatch = pathname.match(/^\/classes\/([^/]+)\/sheet$/);
  if (classMatch?.[1])
    return <ClassSheetPage classId={decodeURIComponent(classMatch[1])} />;
  if (pathname === "/close/today") return <CloseTodayPage />;
  if (pathname === "/close/week") return <PeriodClosePage period="week" />;
  if (pathname === "/close/month") return <PeriodClosePage period="month" />;
  if (pathname === "/classes") return <ClassesPage />;
  if (pathname === "/students") return <StudentsPage />;
  const studentMatch = pathname.match(/^\/students\/([^/]+)\/ledger$/);
  if (studentMatch?.[1])
    return (
      <StudentLedgerPage studentId={decodeURIComponent(studentMatch[1])} />
    );
  const exceptionMatch = pathname.match(/^\/exceptions\/([^/]+)$/);
  if (exceptionMatch?.[1])
    return (
      <ExceptionPage exceptionId={decodeURIComponent(exceptionMatch[1])} />
    );
  if (pathname === "/billing/reconcile") return <BillingPage />;
  if (pathname === "/payroll/close") return <PayrollPage />;
  if (pathname === "/records") return <RecordsPage />;
  if (pathname === "/guardian-drafts") return <GuardianDraftsPage />;
  if (pathname === "/settings/team") return <TeamSettingsPage />;
  if (pathname === "/settings/data" || pathname === "/settings/export")
    return <DataSettingsPage />;
  if (pathname === "/settings/plan") return <PlanSettingsPage />;
  if (pathname === "/onboarding/import") return <ImportPage />;
  if (pathname === "/diagnosis") return <DiagnosisPage />;
  if (pathname === "/pricing") return <PricingPage />;
  if (pathname === "/sign-in") return <SignInPage />;
  if (pathname === "/support") return <SupportPage />;
  if (pathname === "/privacy") return <PrivacyPage />;
  if (pathname === "/terms") return <TermsPage />;
  if (pathname === "/status") return <StatusPage />;
  const guardianMatch = pathname.match(/^\/g\/([^/]+)$/);
  if (guardianMatch?.[1])
    return <GuardianLinkPage token={decodeURIComponent(guardianMatch[1])} />;
  return <NotFoundPage />;
}

export default function App() {
  return (
    <NavigationProvider>
      <WorkspaceProvider>
        <Routes />
      </WorkspaceProvider>
    </NavigationProvider>
  );
}
